import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { CalendarCheck, Clock, Euro, Wallet, CheckCircle2, AlertTriangle, Users, Plus, Trash2, HardHat, ChevronLeft, ChevronRight, Pencil, X, Layers } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function monthBounds(year, month) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  return { first, last };
}
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const emptyManual = {
  editingId: null,
  operaio_id: "",
  custom_nome: "",
  mode: "registered",
  cantiere_id: "",
  data: "",
  ore_ordinarie: 8,
  ore_straordinarie: 0,
  tariffa_ordinaria: 12,
  tariffa_straordinaria: 18,
  tipo: "presenza",
  note: "",
};

export default function Presenze() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(today);

  const [calendarData, setCalendarData] = useState({});
  const [dayList, setDayList] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);

  const [filtCantiere, setFiltCantiere] = useState("all");
  const [filtOperaio, setFiltOperaio] = useState("all");

  const [busteData, setBusteData] = useState({ righe: [] });
  const [payDialog, setPayDialog] = useState({ open: false, riga: null, importo: "", data_pagamento: isoDate(today), note: "" });
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ ...emptyManual, data: isoDate(today) });
  const [customSuggestions, setCustomSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Multi-select calendario
  const [multiMode, setMultiMode] = useState(false);
  const [multiDays, setMultiDays] = useState([]); // array of iso dates
  const [bulkOpen, setBulkOpen] = useState(false);
  const pressTimerRef = useRef(null);
  const longPressedRef = useRef(false);

  const monthDate = useMemo(() => new Date(year, month - 1, 1), [year, month]);

  const loadStatic = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/cantieri"),
        isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setCantieri(c.data || []);
      setOperai((u.data || []).filter((x) => x.role === "operaio" || x.role === "capocantiere"));
    } catch { }
  };

  const loadCalendar = async () => {
    try {
      const params = { year, month };
      if (filtCantiere !== "all") params.cantiere_id = filtCantiere;
      if (filtOperaio !== "all") params.operaio_id = filtOperaio;
      const { data } = await api.get("/presenze/calendar", { params });
      setCalendarData(data.giorni || {});
    } catch { setCalendarData({}); }
  };

  const loadDay = async (d) => {
    try {
      const params = {};
      if (filtCantiere !== "all") params.cantiere_id = filtCantiere;
      if (filtOperaio !== "all") params.operaio_id = filtOperaio;
      const { data } = await api.get(`/presenze/day/${isoDate(d)}`, { params });
      setDayList(data || []);
    } catch { setDayList([]); }
  };

  const loadBuste = async () => {
    if (!isAdmin) return;
    try {
      const params = { year, month };
      if (filtCantiere !== "all") params.cantiere_id = filtCantiere;
      if (filtOperaio !== "all") params.operaio_id = filtOperaio;
      const { data } = await api.get("/buste-paga", { params });
      setBusteData(data || { righe: [] });
    } catch { setBusteData({ righe: [] }); }
  };

  useEffect(() => { loadStatic(); }, []);
  useEffect(() => { loadCalendar(); loadBuste(); /* eslint-disable-next-line */ }, [year, month, filtCantiere, filtOperaio]);
  useEffect(() => { loadDay(selectedDay); /* eslint-disable-next-line */ }, [selectedDay, filtCantiere, filtOperaio]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
    const b = monthBounds(y, m);
    const cand = new Date(y, m - 1, Math.min(selectedDay.getDate(), b.last.getDate()));
    setSelectedDay(cand);
  };

  const presentDays = useMemo(() => {
    return Object.keys(calendarData).map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
  }, [calendarData]);

  const multiDaysDates = useMemo(() => multiDays.map((iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  }), [multiDays]);

  // ---- Long-press handling ----
  const startPress = (date) => {
    longPressedRef.current = false;
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      if (navigator.vibrate) { try { navigator.vibrate(40); } catch { } }
      setMultiMode(true);
      const iso = isoDate(date);
      setMultiDays((prev) => (prev.includes(iso) ? prev : [...prev, iso]));
    }, 500);
  };
  const cancelPress = () => { clearTimeout(pressTimerRef.current); };

  const toggleMultiDay = (date) => {
    const iso = isoDate(date);
    setMultiDays((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso]);
  };

  const exitMultiMode = () => {
    setMultiMode(false);
    setMultiDays([]);
  };

  const onDayClickHandler = (d) => {
    if (longPressedRef.current) {
      longPressedRef.current = false;
      return;
    }
    if (multiMode) {
      toggleMultiDay(d);
      return;
    }
    setSelectedDay(d);
  };

  // ---- CRUD presenze ----
  const savePagamento = async () => {
    if (!payDialog.riga) return;
    const imp = Number(payDialog.importo);
    if (!(imp > 0)) { toast.error("Inserisci un importo > 0"); return; }
    try {
      await api.post("/pagamenti-mensili", {
        operaio_id: payDialog.riga.operaio_id,
        year, month,
        importo: imp,
        data_pagamento: payDialog.data_pagamento,
        note: payDialog.note,
      });
      toast.success("Pagamento registrato");
      setPayDialog({ open: false, riga: null, importo: "", data_pagamento: isoDate(today), note: "" });
      loadBuste();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const removePagamento = async (pid) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    try { await api.delete(`/pagamenti-mensili/${pid}`); toast.success("Rimosso"); loadBuste(); }
    catch { toast.error("Errore"); }
  };

  // Apre modale "Nuova presenza manuale"
  const openNewManual = (preset = {}) => {
    setManual({
      ...emptyManual,
      data: isoDate(selectedDay),
      ...preset,
    });
    setManualOpen(true);
  };

  // Apre modale di modifica completa precompilata
  const openEditManual = (p) => {
    const hasRegistered = !!p.operaio_id;
    setManual({
      editingId: p.id,
      mode: hasRegistered ? "registered" : "custom",
      operaio_id: p.operaio_id || "",
      custom_nome: p.custom_nome || "",
      cantiere_id: p.cantiere_id || "",
      data: p.data || isoDate(selectedDay),
      ore_ordinarie: p.ore_ordinarie ?? p.ore ?? 8,
      ore_straordinarie: p.ore_straordinarie ?? 0,
      tariffa_ordinaria: p.tariffa_ordinaria ?? 0,
      tariffa_straordinaria: p.tariffa_straordinaria ?? 0,
      tipo: p.tipo || "presenza",
      note: p.note || "",
    });
    setManualOpen(true);
  };

  const saveManual = async () => {
    if (manual.tipo === "presenza") {
      if (manual.mode === "registered" && !manual.operaio_id) { toast.error("Seleziona un operaio dalla rubrica"); return; }
      if (manual.mode === "custom" && !manual.custom_nome?.trim()) { toast.error("Inserisci il nome dell'operaio esterno"); return; }
      if (!manual.cantiere_id) { toast.error("Seleziona un cantiere"); return; }
    } else {
      if (!manual.operaio_id) { toast.error("Seleziona l'operaio per questa assenza"); return; }
    }
    const payload = {
      operaio_id: manual.mode === "registered" || manual.tipo !== "presenza" ? (manual.operaio_id || null) : null,
      custom_nome: manual.mode === "custom" && manual.tipo === "presenza" ? manual.custom_nome.trim() : "",
      cantiere_id: manual.tipo === "presenza" ? manual.cantiere_id : null,
      data: manual.data,
      tipo: manual.tipo || "presenza",
      ore_ordinarie: Number(manual.ore_ordinarie),
      ore_straordinarie: Number(manual.ore_straordinarie),
      tariffa_ordinaria: Number(manual.tariffa_ordinaria),
      tariffa_straordinaria: Number(manual.tariffa_straordinaria),
      giornata: "intera",
      note: manual.note || "",
    };
    try {
      if (manual.editingId) {
        await api.put(`/presenze/${manual.editingId}`, payload);
        toast.success("Presenza aggiornata");
      } else {
        await api.post("/presenze", payload);
        toast.success("Presenza registrata");
      }
      setManualOpen(false);
      loadDay(selectedDay); loadCalendar(); loadBuste();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const deleteFromManual = async () => {
    if (!manual.editingId) return;
    if (!window.confirm("Eliminare definitivamente questa presenza?")) return;
    try {
      await api.delete(`/presenze/${manual.editingId}`);
      toast.success("Presenza eliminata");
      setManualOpen(false);
      loadDay(selectedDay); loadCalendar(); loadBuste();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const deleteRow = async (p) => {
    if (!window.confirm(`Eliminare la presenza di ${p.operaio_nome || "operaio"} del ${p.data}?`)) return;
    try {
      await api.delete(`/presenze/${p.id}`);
      toast.success("Presenza eliminata");
      loadDay(selectedDay); loadCalendar(); loadBuste();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---- Bulk create per multi-select ----
  const [bulk, setBulk] = useState({ ...emptyManual, data: "" });
  const openBulkDialog = () => {
    if (multiDays.length === 0) return toast.error("Nessun giorno selezionato");
    setBulk({ ...emptyManual, data: "" });
    setBulkOpen(true);
  };
  const saveBulk = async () => {
    if (bulk.tipo === "presenza") {
      if (bulk.mode === "registered" && !bulk.operaio_id) { toast.error("Seleziona un operaio"); return; }
      if (bulk.mode === "custom" && !bulk.custom_nome?.trim()) { toast.error("Inserisci il nome operaio"); return; }
      if (!bulk.cantiere_id) { toast.error("Seleziona un cantiere"); return; }
    }
    const base = {
      operaio_id: bulk.mode === "registered" ? (bulk.operaio_id || null) : null,
      custom_nome: bulk.mode === "custom" ? bulk.custom_nome.trim() : "",
      cantiere_id: bulk.cantiere_id,
      tipo: bulk.tipo || "presenza",
      ore_ordinarie: Number(bulk.ore_ordinarie),
      ore_straordinarie: Number(bulk.ore_straordinarie),
      tariffa_ordinaria: Number(bulk.tariffa_ordinaria),
      tariffa_straordinaria: Number(bulk.tariffa_straordinaria),
      giornata: "intera",
      note: bulk.note || "",
    };
    let ok = 0, fail = 0;
    for (const iso of multiDays) {
      try {
        await api.post("/presenze", { ...base, data: iso });
        ok += 1;
      } catch { fail += 1; }
    }
    if (ok > 0) toast.success(`${ok} presenze create${fail > 0 ? ` · ${fail} errori` : ""}`);
    else toast.error("Nessuna presenza creata");
    setBulkOpen(false);
    exitMultiMode();
    loadDay(selectedDay); loadCalendar(); loadBuste();
  };

  // ---- Autocomplete suggerimenti nomi custom ----
  useEffect(() => {
    if (manual.mode !== "custom") { setCustomSuggestions([]); return; }
    const q = (manual.custom_nome || "").trim();
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/presenze/custom-names-suggest", { params: { q } });
        setCustomSuggestions(data || []);
      } catch { setCustomSuggestions([]); }
    }, 180);
    return () => clearTimeout(h);
  }, [manual.custom_nome, manual.mode]);

  const fetchLastRate = async ({ operaio_id, custom_nome }, target = "manual") => {
    try {
      const params = {};
      if (operaio_id) params.operaio_id = operaio_id;
      else if (custom_nome) params.custom_nome = custom_nome;
      else return;
      const { data } = await api.get("/presenze/last-rate", { params });
      if (data?.found) {
        const upd = (m) => ({
          ...m,
          tariffa_ordinaria: data.tariffa_ordinaria || m.tariffa_ordinaria,
          tariffa_straordinaria: data.tariffa_straordinaria || m.tariffa_straordinaria,
        });
        if (target === "bulk") setBulk(upd); else setManual(upd);
        toast.info(`Tariffe auto-compilate dall'ultima presenza (${data.ultima_data})`);
      }
    } catch { }
  };

  const pickCustomSuggestion = (nome) => {
    setManual((m) => ({ ...m, custom_nome: nome }));
    setShowSuggestions(false);
    fetchLastRate({ custom_nome: nome });
  };

  const totMese = (busteData.righe || []).reduce((s, r) => s + (r.totale_dovuto || 0), 0);
  const totPagato = (busteData.righe || []).reduce((s, r) => s + (r.pagato_mese || 0), 0);
  const totResiduo = (busteData.righe || []).reduce((s, r) => s + (r.residuo || 0), 0);

  const presenzeOggi = Object.keys(calendarData).length;
  const totOperaiMese = new Set((busteData.righe || []).map((r) => r.operaio_id)).size;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Timesheet</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Presenze & Buste Paga</h1>
          <p className="text-muted-foreground mt-1">
            Visualizza presenze su calendario e gestisci i pagamenti mensili dei dipendenti.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-2 py-1">
          <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-sm font-bold min-w-[150px] text-center" data-testid="month-label">{MESI[month - 1]} {year}</div>
          <Button variant="ghost" size="icon" onClick={() => changeMonth(1)} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        {isAdmin && (
          <Button data-testid="add-manual-presenza" size="sm" onClick={() => openNewManual()}>
            <Plus className="h-4 w-4 mr-1" /> Presenza manuale
          </Button>
        )}
      </div>

      {/* Filtri globali */}
      <Card className="card-elevated">
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-wider">Cantiere</Label>
            <Select value={filtCantiere} onValueChange={setFiltCantiere}>
              <SelectTrigger data-testid="flt-cantiere"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i cantieri</SelectItem>
                {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] uppercase tracking-wider">Dipendente</Label>
              <Select value={filtOperaio} onValueChange={setFiltOperaio}>
                <SelectTrigger data-testid="flt-operaio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i dipendenti</SelectItem>
                  {operai.map((o) => <SelectItem key={o.id} value={o.id}>{o.name || o.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {(filtCantiere !== "all" || filtOperaio !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setFiltCantiere("all"); setFiltOperaio("all"); }}>Pulisci filtri</Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="presenze">
        <TabsList>
          <TabsTrigger value="presenze" data-testid="tab-presenze"><CalendarCheck className="h-4 w-4 mr-2" />Presenze</TabsTrigger>
          {isAdmin && <TabsTrigger value="buste" data-testid="tab-buste"><Wallet className="h-4 w-4 mr-2" />Buste Paga</TabsTrigger>}
        </TabsList>

        <TabsContent value="presenze" className="pt-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="card-elevated"><CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Giorni con presenze (mese)</div>
              <div className="text-2xl font-extrabold" data-testid="kpi-giorni">{presenzeOggi}</div>
            </CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dipendenti coinvolti</div>
              <div className="text-2xl font-extrabold">{totOperaiMese}</div>
            </CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Giorno selezionato</div>
              <div className="text-xl font-extrabold">{selectedDay.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}</div>
              <div className="text-xs text-muted-foreground">{dayList.length} presenze</div>
            </CardContent></Card>
          </div>

          <div className="grid lg:grid-cols-5 gap-4">
            <Card className="card-elevated lg:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Calendario</div>
                  {multiMode && (
                    <Badge variant="secondary" className="text-[10px]" data-testid="multi-mode-badge">
                      <Layers className="h-3 w-3 mr-1" /> Selezione multipla
                    </Badge>
                  )}
                </div>
                <Calendar
                  mode="single"
                  selected={multiMode ? undefined : selectedDay}
                  onDayClick={onDayClickHandler}
                  month={monthDate}
                  onMonthChange={(d) => { setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }}
                  modifiers={{ presenti: presentDays, multi: multiDaysDates }}
                  modifiersClassNames={{
                    presenti: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-200 font-bold",
                    multi: "ring-2 ring-primary ring-offset-1 bg-primary/20 text-primary font-extrabold",
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const iso = isoDate(date);
                      const n = calendarData[iso];
                      return (
                        <div
                          className="relative w-full h-full flex items-center justify-center select-none touch-none"
                          onPointerDown={() => startPress(date)}
                          onPointerUp={cancelPress}
                          onPointerLeave={cancelPress}
                          onPointerCancel={cancelPress}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          <span>{date.getDate()}</span>
                          {n > 0 && (
                            <span className="absolute -bottom-0.5 right-0 text-[9px] px-1 rounded bg-primary text-primary-foreground font-bold leading-tight">
                              {n}
                            </span>
                          )}
                        </div>
                      );
                    },
                  }}
                  className="rounded-md"
                  data-testid="presenze-calendar"
                />
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-100 dark:bg-emerald-900/40 inline-block" /> giorni con presenze</span>
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-primary inline-block" /> N dipendenti</span>
                  <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded ring-2 ring-primary bg-primary/20 inline-block" /> selezione multipla</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  💡 Tieni premuto a lungo (~0,5s) su una data per attivare la selezione multipla.
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated lg:col-span-3">
              <CardContent className="p-0">
                <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Dettaglio giorno</div>
                    <div className="font-bold text-lg">{selectedDay.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                  </div>
                  <Badge variant="secondary" className="text-xs"><Users className="h-3 w-3 mr-1" />{dayList.length} presenze</Badge>
                </div>
                <div className="max-h-[520px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary sticky top-0 z-10">
                      <tr className="text-left">
                        <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Dipendente</th>
                        <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Cantiere</th>
                        <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Ord.</th>
                        <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Str.</th>
                        {isAdmin && <th className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Paga</th>}
                        {isAdmin && <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-muted-foreground">Azioni</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {dayList.map((p) => (
                        <tr
                          key={p.id}
                          className={`border-t border-border ${isAdmin ? "cursor-pointer hover:bg-accent/40" : ""}`}
                          data-testid={`day-row-${p.id}`}
                          onClick={(e) => {
                            // ignora i click sui bottoni di azione
                            if (!isAdmin) return;
                            if (e.target.closest("button")) return;
                            openEditManual(p);
                          }}
                        >
                          <td className="px-4 py-2 font-medium">
                            <div className="flex items-center gap-2"><HardHat className="h-3 w-3 text-primary" />{p.operaio_nome || "—"}</div>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground text-xs">{p.cantiere_nome || "—"}</td>
                          <td className="px-4 py-2 tabular-nums">{p.ore_ordinarie ?? p.ore ?? 0}h</td>
                          <td className="px-4 py-2 tabular-nums text-primary font-semibold">{p.ore_straordinarie ?? 0}h</td>
                          {isAdmin && (
                            <td className="px-4 py-2 tabular-nums font-bold">
                              <div className="flex items-center gap-1">
                                <Euro className="h-3 w-3" />{fmt(p.paga_totale || 0)}
                              </div>
                            </td>
                          )}
                          {isAdmin && (
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); openEditManual(p); }}
                                  data-testid={`edit-presenza-${p.id}`}
                                  title="Modifica presenza"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); deleteRow(p); }}
                                  data-testid={`delete-presenza-${p.id}`}
                                  title="Elimina presenza"
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {dayList.length === 0 && (
                        <tr><td colSpan={isAdmin ? 6 : 4} className="px-4 py-10 text-center text-muted-foreground">
                          Nessuna presenza registrata in questo giorno.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="buste" className="pt-4 space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="card-elevated"><CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale dovuto {MESI[month - 1]}</div>
                <div className="text-2xl font-extrabold text-primary" data-testid="kpi-dovuto">{fmt(totMese)}</div>
                {(busteData.righe || []).some((r) => r.arretrato > 0) && (
                  <div className="text-[11px] text-muted-foreground mt-1">include arretrati non pagati</div>
                )}
              </CardContent></Card>
              <Card className="card-elevated"><CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Già pagato</div>
                <div className="text-2xl font-extrabold text-emerald-600">{fmt(totPagato)}</div>
              </CardContent></Card>
              <Card className="card-elevated"><CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Residuo da pagare</div>
                <div className="text-2xl font-extrabold text-destructive" data-testid="kpi-residuo">{fmt(totResiduo)}</div>
              </CardContent></Card>
            </div>

            <Card className="card-elevated">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Dipendente</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Gg</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Ord.</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Str.</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Paga mese</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Arretrato</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Totale</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Pagato</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Residuo</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(busteData.righe || []).map((r) => (
                      <tr key={r.operaio_id} className="border-t border-border" data-testid={`buste-row-${r.operaio_id}`}>
                        <td className="px-4 py-3 font-medium">
                          <div className="flex items-center gap-2"><HardHat className="h-3 w-3 text-primary" />{r.operaio_nome}</div>
                          {r.cantieri?.length > 0 && (
                            <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.cantieri.join(", ")}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{r.giorni_lavorati}</td>
                        <td className="px-4 py-3 tabular-nums">{r.ore_ordinarie}h</td>
                        <td className="px-4 py-3 tabular-nums text-primary">{r.ore_straordinarie}h</td>
                        <td className="px-4 py-3 tabular-nums">{fmt(r.paga_mese)}</td>
                        <td className="px-4 py-3 tabular-nums">
                          {r.arretrato > 0 ? <span className="text-amber-600 font-semibold" title="Arretrato mesi precedenti">{fmt(r.arretrato)}</span> : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 tabular-nums font-bold">
                          {fmt(r.totale_dovuto)}
                          {r.arretrato > 0 && <div className="text-[10px] text-amber-600">include arretrati</div>}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-emerald-600 font-semibold">{fmt(r.pagato_mese)}</td>
                        <td className="px-4 py-3 tabular-nums font-bold">
                          {r.residuo > 0 ? <span className="text-destructive">{fmt(r.residuo)}</span>
                            : r.totale_dovuto > 0 ? <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Saldato</span>
                              : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {r.residuo > 0 && (
                              <Button
                                data-testid={`pay-${r.operaio_id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => setPayDialog({ open: true, riga: r, importo: r.residuo.toFixed(2), data_pagamento: isoDate(today), note: "" })}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Pagamento
                              </Button>
                            )}
                            {r.residuo > 0 && (
                              <Button
                                data-testid={`pay-full-${r.operaio_id}`}
                                size="sm"
                                onClick={() => setPayDialog({ open: true, riga: r, importo: r.residuo.toFixed(2), data_pagamento: isoDate(today), note: "Saldo intero" })}
                              >
                                <Wallet className="h-3 w-3 mr-1" /> Saldo
                              </Button>
                            )}
                          </div>
                          {r.pagamenti?.length > 0 && (
                            <details className="mt-1 text-left">
                              <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">{r.pagamenti.length} pagamenti</summary>
                              <div className="space-y-1 mt-1">
                                {r.pagamenti.map((p) => (
                                  <div key={p.id} className="flex items-center justify-between text-[11px] bg-secondary rounded px-2 py-1 gap-1">
                                    <span>{p.data_pagamento} · <strong>{fmt(p.importo)}</strong>{p.note ? ` · ${p.note}` : ""}</span>
                                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removePagamento(p.id)}>
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(busteData.righe || []).length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Nessuna busta paga per questo mese.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Barra flottante selezione multipla */}
      {multiMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-card border border-primary shadow-2xl rounded-full px-4 py-2 flex items-center gap-3" data-testid="multi-action-bar">
          <Layers className="h-4 w-4 text-primary" />
          <span className="text-sm font-bold">{multiDays.length} giorn{multiDays.length === 1 ? "o" : "i"} selezionat{multiDays.length === 1 ? "o" : "i"}</span>
          {isAdmin && (
            <Button size="sm" data-testid="multi-create-btn" disabled={multiDays.length === 0} onClick={openBulkDialog}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Crea presenze
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={exitMultiMode} data-testid="multi-cancel-btn">
            <X className="h-3.5 w-3.5 mr-1" /> Annulla
          </Button>
        </div>
      )}

      {/* MODALE Nuova / Modifica presenza */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{manual.editingId ? "Modifica presenza" : "Nuova presenza manuale"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex gap-1 bg-secondary rounded-lg p-1 text-xs">
              <button
                data-testid="mp-mode-registered"
                onClick={() => setManual({ ...manual, mode: "registered", custom_nome: "" })}
                className={`flex-1 py-1.5 rounded-md font-semibold transition ${manual.mode === "registered" ? "bg-background shadow" : "text-muted-foreground"}`}
              >Dalla rubrica</button>
              <button
                data-testid="mp-mode-custom"
                onClick={() => setManual({ ...manual, mode: "custom", operaio_id: "" })}
                className={`flex-1 py-1.5 rounded-md font-semibold transition ${manual.mode === "custom" ? "bg-background shadow" : "text-muted-foreground"}`}
              >Operaio esterno</button>
            </div>
            {manual.mode === "registered" ? (
              <div className="col-span-2">
                <Label>Operaio dalla rubrica</Label>
                <Select
                  value={manual.operaio_id}
                  onValueChange={(v) => {
                    setManual({ ...manual, operaio_id: v });
                    fetchLastRate({ operaio_id: v });
                  }}
                >
                  <SelectTrigger data-testid="mp-operaio"><SelectValue placeholder="Seleziona dalla squadra" /></SelectTrigger>
                  <SelectContent>{operai.map((o) => <SelectItem key={o.id} value={o.id}>{o.name || o.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="col-span-2 relative">
                <Label>Nome operaio esterno</Label>
                <Input
                  data-testid="mp-custom-nome"
                  value={manual.custom_nome}
                  onChange={(e) => { setManual({ ...manual, custom_nome: e.target.value }); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Es. Mario Bianchi (saldatore in subappalto)"
                  autoComplete="off"
                />
                {showSuggestions && customSuggestions.length > 0 && (
                  <div data-testid="mp-custom-suggestions" className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover shadow-lg">
                    {customSuggestions.map((s) => (
                      <button
                        key={s.nome}
                        type="button"
                        onClick={() => pickCustomSuggestion(s.nome)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center justify-between gap-2"
                      >
                        <span className="font-medium truncate">{s.nome}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{s.count} pres · ultima {s.ultima_data}</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">Per operai non registrati nell'app.</p>
              </div>
            )}
            <div className="col-span-2">
              <Label>Cantiere</Label>
              <Select value={manual.cantiere_id} onValueChange={(v) => setManual({ ...manual, cantiere_id: v })}>
                <SelectTrigger data-testid="mp-cantiere"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input data-testid="mp-data" type="date" value={manual.data} onChange={(e) => setManual({ ...manual, data: e.target.value })} /></div>
            <div><Label>Note</Label><Input data-testid="mp-note" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} /></div>
            <div><Label>Ore ord.</Label><Input data-testid="mp-ore-ord" type="number" step="0.5" min="0" value={manual.ore_ordinarie} onChange={(e) => setManual({ ...manual, ore_ordinarie: e.target.value })} /></div>
            <div><Label>Ore straord.</Label><Input data-testid="mp-ore-str" type="number" step="0.5" min="0" value={manual.ore_straordinarie} onChange={(e) => setManual({ ...manual, ore_straordinarie: e.target.value })} /></div>
            <div><Label>€/h ord.</Label><Input data-testid="mp-tar-ord" type="number" step="0.5" value={manual.tariffa_ordinaria} onChange={(e) => setManual({ ...manual, tariffa_ordinaria: e.target.value })} /></div>
            <div><Label>€/h straord.</Label><Input data-testid="mp-tar-str" type="number" step="0.5" value={manual.tariffa_straordinaria} onChange={(e) => setManual({ ...manual, tariffa_straordinaria: e.target.value })} /></div>
            <div className="col-span-2 bg-secondary rounded-lg p-2 text-sm flex justify-between">
              <span>Paga totale:</span>
              <strong className="text-primary">{fmt(Number(manual.ore_ordinarie || 0) * Number(manual.tariffa_ordinaria || 0) + Number(manual.ore_straordinarie || 0) * Number(manual.tariffa_straordinaria || 0))}</strong>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button data-testid="mp-save" onClick={saveManual} className="h-11 flex-1">
              {manual.editingId ? "Salva modifiche" : "Registra presenza"}
            </Button>
            {manual.editingId && isAdmin && (
              <Button
                data-testid="mp-delete"
                variant="outline"
                onClick={deleteFromManual}
                className="h-11 border-destructive/60 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Elimina presenza
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* MODALE Bulk Create (selezione multipla) */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              Crea presenze su {multiDays.length} giorn{multiDays.length === 1 ? "o" : "i"}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-secondary rounded-lg p-2 text-[11px] max-h-20 overflow-y-auto">
            {[...multiDays].sort().map((d) => (
              <span key={d} className="inline-block bg-background border rounded px-1.5 py-0.5 mr-1 mb-1 font-mono">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex gap-1 bg-secondary rounded-lg p-1 text-xs">
              <button
                data-testid="bulk-mode-registered"
                onClick={() => setBulk({ ...bulk, mode: "registered", custom_nome: "" })}
                className={`flex-1 py-1.5 rounded-md font-semibold ${bulk.mode === "registered" ? "bg-background shadow" : "text-muted-foreground"}`}
              >Dalla rubrica</button>
              <button
                data-testid="bulk-mode-custom"
                onClick={() => setBulk({ ...bulk, mode: "custom", operaio_id: "" })}
                className={`flex-1 py-1.5 rounded-md font-semibold ${bulk.mode === "custom" ? "bg-background shadow" : "text-muted-foreground"}`}
              >Operaio esterno</button>
            </div>
            {bulk.mode === "registered" ? (
              <div className="col-span-2">
                <Label>Operaio</Label>
                <Select
                  value={bulk.operaio_id}
                  onValueChange={(v) => {
                    setBulk({ ...bulk, operaio_id: v });
                    fetchLastRate({ operaio_id: v }, "bulk");
                  }}
                >
                  <SelectTrigger data-testid="bulk-operaio"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{operai.map((o) => <SelectItem key={o.id} value={o.id}>{o.name || o.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="col-span-2">
                <Label>Nome operaio esterno</Label>
                <Input data-testid="bulk-custom-nome" value={bulk.custom_nome} onChange={(e) => setBulk({ ...bulk, custom_nome: e.target.value })} />
              </div>
            )}
            <div className="col-span-2">
              <Label>Cantiere</Label>
              <Select value={bulk.cantiere_id} onValueChange={(v) => setBulk({ ...bulk, cantiere_id: v })}>
                <SelectTrigger data-testid="bulk-cantiere"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ore ord.</Label><Input type="number" step="0.5" min="0" value={bulk.ore_ordinarie} onChange={(e) => setBulk({ ...bulk, ore_ordinarie: e.target.value })} /></div>
            <div><Label>Ore straord.</Label><Input type="number" step="0.5" min="0" value={bulk.ore_straordinarie} onChange={(e) => setBulk({ ...bulk, ore_straordinarie: e.target.value })} /></div>
            <div><Label>€/h ord.</Label><Input type="number" step="0.5" value={bulk.tariffa_ordinaria} onChange={(e) => setBulk({ ...bulk, tariffa_ordinaria: e.target.value })} /></div>
            <div><Label>€/h straord.</Label><Input type="number" step="0.5" value={bulk.tariffa_straordinaria} onChange={(e) => setBulk({ ...bulk, tariffa_straordinaria: e.target.value })} /></div>
            <div className="col-span-2"><Label>Note (applicate a tutti i giorni)</Label><Input value={bulk.note} onChange={(e) => setBulk({ ...bulk, note: e.target.value })} /></div>
          </div>
          <Button data-testid="bulk-save" onClick={saveBulk} className="h-11 mt-2 w-full">
            Crea {multiDays.length} presenze
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={payDialog.open} onOpenChange={(o) => setPayDialog({ ...payDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registra pagamento {MESI[month - 1]} {year}</DialogTitle></DialogHeader>
          {payDialog.riga && (
            <div className="space-y-3">
              <div className="bg-secondary rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Dipendente</span><span className="font-bold">{payDialog.riga.operaio_nome}</span></div>
                <div className="flex justify-between"><span>Totale dovuto {MESI[month - 1]}</span><span className="font-bold text-primary">{fmt(payDialog.riga.totale_dovuto)}</span></div>
                {payDialog.riga.arretrato > 0 && (
                  <div className="flex justify-between text-amber-600"><span>di cui arretrato</span><span>{fmt(payDialog.riga.arretrato)}</span></div>
                )}
                <div className="flex justify-between"><span>Già pagato</span><span className="font-bold text-emerald-600">{fmt(payDialog.riga.pagato_mese)}</span></div>
                <div className="flex justify-between"><span>Residuo</span><span className="font-bold text-destructive">{fmt(payDialog.riga.residuo)}</span></div>
              </div>
              <div><Label>Importo pagato €</Label>
                <Input data-testid="pay-importo" type="number" step="0.01" value={payDialog.importo}
                  onChange={(e) => setPayDialog({ ...payDialog, importo: e.target.value })} />
              </div>
              <div><Label>Data pagamento</Label>
                <Input type="date" value={payDialog.data_pagamento}
                  onChange={(e) => setPayDialog({ ...payDialog, data_pagamento: e.target.value })} />
              </div>
              <div><Label>Note (opzionale)</Label>
                <Textarea rows={2} value={payDialog.note}
                  onChange={(e) => setPayDialog({ ...payDialog, note: e.target.value })} />
              </div>
              <Button data-testid="pay-save" onClick={savePagamento} className="w-full h-11">
                <Wallet className="h-4 w-4 mr-2" /> Registra pagamento
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
